import { useCallback, useMemo, useState } from 'react';
import { TEXT_FIELD_INVALID_CHARS_REGEX } from '../constants';

/**
 * Pure check for WAF-unsafe characters. Centralized in `common` so web and mobile
 * can validate against the same rule (`TEXT_FIELD_INVALID_CHARS_REGEX`) once both
 * platforms are wired to use it.
 * Returns `true` when the value contains at least one disallowed character.
 */
export const hasInvalidChars = (value: unknown): boolean => TEXT_FIELD_INVALID_CHARS_REGEX.test(String(value ?? ''));

type UseTextFieldErrorReturn = {
    hasTextError: boolean;
    makeErrorHandler: (field: string) => (hasError: boolean) => void;
    resetErrors: () => void;
};

export function useTextFieldError(): UseTextFieldErrorReturn {
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    const hasTextError = useMemo(() => Object.values(errors).some(Boolean), [errors]);

    const makeErrorHandler = useCallback(
        (field: string) =>
            (hasError: boolean): void => {
                setErrors((prev) => {
                    if (prev[field] === hasError) {
                        return prev;
                    }
                    return { ...prev, [field]: hasError };
                });
            },
        []
    );

    const resetErrors = useCallback((): void => setErrors({}), []);

    return { hasTextError, makeErrorHandler, resetErrors };
}
